import { CloseOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { FC, useEffect, useRef, useState } from 'react';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { rolesMap } from 'src/constants/rolesList';
import { UserRoles } from '@dynamika-shared/types/auth';
import { invitationValidationSchema } from './invitationValidationSchema';
import { formatFormErrors } from '@utils/formatFormErrors';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';

import { useInvitationsUserManagementSlice } from '@restore/invitationsUserManagement/invitationsUserManagement.slice';
import { useFacilitiesSlice } from '@restore/facilities/facilities.slice';
import type { CreateInvitationRequest } from '@restore/invitationsUserManagement/invitationsUserManagement.types';

type CreateInvitationProps = {
  close: () => void;
  refetchInvitations: () => void;
  sponsor: { id: string; name: string };
};

export const CreateInvitation: FC<CreateInvitationProps> = ({
  close,
  refetchInvitations,
  sponsor,
}) => {
  const { setValue, trigger, watch, getValues, formState, setError } = useForm({
    resolver: invitationValidationSchema,
    defaultValues: {
      email: '',
      role: UserRoles.SITE,
      facilityId: '',
      sponsorId: sponsor.id,
    },
  });

  const [isAskQuitOpen, setIsAskQuitOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [createInvitation, actionLoading, error] =
    useInvitationsUserManagementSlice((state, actions) => [
      actions.createInvitation,
      state.actionLoading,
      state.error,
    ]);

  const [facilities, fetchFacilities] = useFacilitiesSlice(
    (state, actions) => [state.data, actions.fetchFacilities]
  );

  const { isDirty, isValid, errors } = formState;

  const isSubmitDisabled = !isValid || isSubmitting || !isDirty || actionLoading;

  const wasLoadingRef = useRef(false);

  const onChangeField = (fieldName: keyof CreateInvitationRequest, value: string) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };

  const onBlur = (fieldName: keyof CreateInvitationRequest, value: string) => {
    onChangeField(fieldName, value.trim());
  };

  const submit = () => {
    setIsSubmitting(true);
    createInvitation(getValues());
  };

  useEffect(() => {
    if (wasLoadingRef.current && !actionLoading) {
      setIsSubmitting(false);
      if (!error) {
        close();
        refetchInvitations();
      } else {
        const detail = error.response?.data?.detail;
        const email = getValues('email');
        if (detail?.includes(email)) {
          setError('email', { type: 'manual', message: detail });
        }
      }
    }
    wasLoadingRef.current = actionLoading;
  }, [actionLoading, error, close, refetchInvitations, getValues, setError]);

  const onCloseDrawer = () => {
    if (!isDirty) {
      close();
    } else {
      setIsAskQuitOpen(true);
    }
  };

  const facilityOptions = facilities.map(f => ({
    label: f.name,
    value: f.id,
  }));

  const roleOptions = [rolesMap[UserRoles.SITE]];
  const sponsorOptions = [{ label: sponsor.name, value: sponsor.id }];

  useEffect(() => {
    fetchFacilities(undefined);
  }, [fetchFacilities]);

  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title='Invite User'
      open
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined />}
      data-testid={'createInvitationDrawer'}
    >
      <form className='overflow-auto'>
        <div className='flex w-full flex-col gap-3 pr-2'>
          <Input
            required
            maxLength={320}
            value={watch('email')}
            name='email'
            data-testid='email'
            onChange={e => onChangeField('email', e.target.value)}
            onBlur={e => onBlur('email', e.target.value)}
            errors={formatFormErrors(errors.email?.message)}
            label={'Email'}
            placeholder={'Email'}
          />
          <Select
            required
            disabled
            errors={errors.role?.message}
            options={roleOptions}
            value={watch('role') || undefined}
            name='role'
            label={'Role'}
            placeholder={'Select Role'}
            data-testid='role'
          />
          <Select
            required
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            errors={errors.facilityId?.message}
            options={facilityOptions}
            value={watch('facilityId') || undefined}
            name='facilityId'
            onChange={e => onChangeField('facilityId', e)}
            label={'Facility'}
            placeholder={'Select Facility'}
            data-testid='facilityId'
          />
          <Select
            required
            disabled
            errors={errors.sponsorId?.message}
            options={sponsorOptions}
            value={watch('sponsorId') || undefined}
            name='sponsorId'
            label={'Sponsor'}
            placeholder={'Select Sponsor'}
            data-testid='sponsorId'
          />
        </div>
      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button className='w-[17rem]' onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button
            className='w-[17rem]'
            type='primary'
            disabled={isSubmitDisabled}
            onClick={submit}
          >
            Create
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        open={isAskQuitOpen}
        onConfirm={close}
        onCancel={() => setIsAskQuitOpen(false)}
      />
    </Drawer>
  );
};
